import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoComarca from '../assets/images/logo/vertical.png';
import type { CotizacionResponse } from '../services/landing/cotizacionService';
import { fechaCotizacion } from './quotationDates';

export const generarPDFCotizaciones = async (quotes: CotizacionResponse['cotizacion'][]): Promise<Blob | null> => {
    const cotizacionesFinales = quotes.map(resultado => ({ resultado, pendiente: {
        dias: resultado.detalle_pdf?.dias, horas: resultado.detalle_pdf?.horas,
        unidad: resultado.detalle_pdf?.unidad || 'dia', cantidadPeriodos: resultado.cantidad_periodos,
        precioUnitario: resultado.detalle_pdf?.precio_unitario ?? resultado.subtotal / resultado.cantidad_periodos,
        personas: resultado.detalle_pdf?.personas || 1, cantidadPersonasEspacio: resultado.detalle_pdf?.capacidad,
        espacioDescripcion: resultado.detalle_pdf?.descripcion_espacio || resultado.espacio,
        horaInicio: resultado.hora_inicio || '', horaFin: resultado.hora_fin || '',
    } }));
        if (cotizacionesFinales.length === 0) return null;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const margin = 18;
        const contentW = pageW - margin * 2;
        let y = margin;

        // ── LOGO (using static import resolved by webpack, preserving aspect ratio) ──
        let logoNaturalW = 1;
        let logoNaturalH = 1;
        try {
            const logoDataUrl = await new Promise<string>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = logoComarca;
                img.onload = () => {
                    logoNaturalW = img.naturalWidth;
                    logoNaturalH = img.naturalHeight;
                    const canvas = document.createElement('canvas');
                    canvas.width = logoNaturalW;
                    canvas.height = logoNaturalH;
                    canvas.getContext('2d')?.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = () => resolve('');
            });
            if (logoDataUrl) {
                // Fixed width, calculate height to preserve natural ratio
                const logoW = 26;
                const logoH = (logoNaturalH / logoNaturalW) * logoW;
                doc.addImage(logoDataUrl, 'PNG', pageW - margin - logoW, y, logoW, logoH);
            }
        } catch (_) {
            // Logo loading failed silently — continue without it
        }

        // ── TITLE ──
        y += 8;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(30, 30, 30);
        doc.text('CO MARCA COWORK - COTIZACIÓN DE ESPACIOS', pageW / 2, y, { align: 'center' });
        y += 7;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        const primeraFinal = cotizacionesFinales[0].resultado;
        const clienteNombreCompleto = `${primeraFinal.cliente.nombre}`;
        doc.text(`Cliente: ${clienteNombreCompleto}`, pageW / 2, y, { align: 'center' });
        y += 5;
        const ahora = new Date(primeraFinal.creada_en || Date.now());
        const fechaHoraStr = fechaCotizacion(ahora.toLocaleDateString('en-CA', {
            timeZone: 'America/La_Paz', day: '2-digit', month: '2-digit', year: 'numeric'
        })) + ' a las ' + ahora.toLocaleTimeString('es-BO', {
            timeZone: 'America/La_Paz', hour: '2-digit', minute: '2-digit'
        });
        doc.setFontSize(8.5);
        doc.setTextColor(100, 100, 100);
        doc.text(`Fecha de cotización: ${fechaHoraStr}`, pageW / 2, y, { align: 'center' });
        y += 6;

        // ── SECTION 1: DETALLE DEL SERVICIO (una fila por cotización) ──
        const section1Rows = cotizacionesFinales.map(item => {
            const { pendiente, resultado } = item;
            const hStr = pendiente.unidad === 'hora'
                ? `${resultado.hora_inicio || pendiente.horaInicio} - ${resultado.hora_fin || pendiente.horaFin}`
                : `${resultado.hora_inicio?.slice(0, 5) || '-'} - ${resultado.hora_fin?.slice(0, 5) || 'Por temporizador'}`;
            const capStr = pendiente.cantidadPersonasEspacio
                ? `${pendiente.cantidadPersonasEspacio} persona${pendiente.cantidadPersonasEspacio > 1 ? 's' : ''}`
                : (pendiente.personas ? `${pendiente.personas} persona${pendiente.personas > 1 ? 's' : ''}` : '-');
            const espDesc = pendiente.espacioDescripcion || resultado.espacio || '-';
            return [
                { content: resultado.servicio, styles: { fontStyle: 'bold' as const, fontSize: 8.5 } },
                { content: capStr, styles: { fontSize: 8.5 } },
                { content: hStr, styles: { fontSize: 8.5 } },
                { content: '300 MB\nilimitado\nCABLE LAN', styles: { fontSize: 8.5 } },
                { content: espDesc, styles: { fontSize: 8.5 } },
            ];
        });

        autoTable(doc, {
            startY: y,
            head: [
                [{
                    content: '1. DETALLE DEL SERVICIO COMBINADO DE OFICINAS (FORMATO TODO INCLUIDO)',
                    colSpan: 5,
                    styles: { fillColor: [220, 220, 220], textColor: [20, 20, 20], fontStyle: 'bold' as const, fontSize: 8.5 }
                }]
            ],
            body: [
                [
                    { content: 'Servicio', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'Capacidad', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'Horario', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'Internet', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'Equipo, Espacios e Inclusiones', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                ],
                ...(section1Rows as any),
            ],
            columnStyles: {
                0: { cellWidth: 36 },
                1: { cellWidth: 26 },
                2: { cellWidth: 30 },
                3: { cellWidth: 28 },
                4: { cellWidth: contentW - 120 },
            },
            margin: { left: margin, right: margin },
            styles: { lineColor: [180, 180, 180], lineWidth: 0.3, cellPadding: 3 },
            theme: 'grid',
        });

        y = (doc as any).lastAutoTable.finalY + 3;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(80, 80, 80);
        doc.text('* NOTA: Si su ingreso a Co Marca cowork es por primera vez debe firmar condiciones de políticas de uso para resguardo de ambas partes.', margin, y);
        y += 7;

        // ── SECTION 2: RESUMEN DE PRECIOS (una fila por cotización) ──
        const grandTotalPDF = cotizacionesFinales.reduce((sum, item) => sum + item.resultado.total, 0);
        const section2DataRows = cotizacionesFinales.map(item => {
            const { pendiente, resultado } = item;
            const uLabel = (pendiente.dias || 1) > 1 || (pendiente.unidad === 'hora' && (pendiente.dias || (pendiente.horas || 1) > 1)) ? 'período(s)' : pendiente.unidad === 'hora' ? 'hora(s)' : pendiente.unidad === 'dia' ? 'día(s)' : 'período(s)';
            return [
                { content: `${resultado.servicio.toUpperCase()} - ${pendiente.cantidadPeriodos} ${uLabel}`, styles: { fontSize: 8.5 } },
                { content: `Bs ${pendiente.precioUnitario.toFixed(2)}`, styles: { fontSize: 8.5 } },
                { content: `Bs ${resultado.total.toFixed(2)}`, styles: { fontSize: 8.5 } },
                { content: `Espacio: ${resultado.espacio || '-'}\n${fechaCotizacion(resultado.fecha_inicio)} a ${fechaCotizacion(resultado.fecha_fin)}\nCódigo: ${resultado.codigo}${resultado.observaciones ? '\n' + resultado.observaciones : ''}`, styles: { fontSize: 8.5 } },
            ];
        });

        autoTable(doc, {
            startY: y,
            head: [
                [{
                    content: '2. RESUMEN DE PRECIOS',
                    colSpan: 4,
                    styles: { fillColor: [220, 220, 220], textColor: [20, 20, 20], fontStyle: 'bold' as const, fontSize: 8.5 }
                }]
            ],
            body: [
                [
                    { content: 'DETALLE', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'PRECIO UNITARIO', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'PRECIO TOTAL', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                    { content: 'OBSERVACIONES', styles: { fontStyle: 'bold' as const, fontSize: 8, fillColor: [240, 240, 240] as [number, number, number] } },
                ],
                ...(section2DataRows as any),
                [
                    { content: 'PRECIO TOTAL', styles: { fontStyle: 'bold' as const, fontSize: 9, fillColor: [245, 245, 245] as [number, number, number] } },
                    { content: '', styles: { fillColor: [245, 245, 245] as [number, number, number] } },
                    { content: `Bs ${grandTotalPDF.toFixed(2)}`, styles: { fontStyle: 'bold' as const, fontSize: 9, fillColor: [245, 245, 245] as [number, number, number] } },
                    { content: '', styles: { fillColor: [245, 245, 245] as [number, number, number] } },
                ],
            ],
            columnStyles: {
                0: { cellWidth: contentW * 0.32 },
                1: { cellWidth: contentW * 0.2 },
                2: { cellWidth: contentW * 0.2 },
                3: { cellWidth: contentW * 0.28 },
            },
            margin: { left: margin, right: margin },
            styles: { lineColor: [180, 180, 180], lineWidth: 0.3, cellPadding: 3 },
            theme: 'grid',
        });

        y = (doc as any).lastAutoTable.finalY + 8;

        // ── SECTION 3 (BOX 4 in template): CONDICIONES DE PAGO ──
        // Section header
        doc.setFillColor(220, 220, 220);
        doc.rect(margin, y, contentW, 7, 'F');
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.3);
        doc.rect(margin, y, contentW, 7, 'S');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(20, 20, 20);
        doc.text('3. CONDICIONES DE PAGO Y DATOS BANCARIOS', margin + 3, y + 4.8);
        y += 9;

        // Conditions body box
        const condLines = primeraFinal.condiciones_pago || [
            ['Condición:', 'El pago se debe realizar al ingresar o al término de la reserva, antes de salir del espacio en su totalidad.'],
        ];

        const boxStartY = y;
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.3);

        condLines.forEach(([label, value]) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(20, 20, 20);
            if (label) {
                doc.text(label, margin + 3, y + 4);
            }
            doc.setFont('helvetica', 'normal');
            const labelWidth = label ? doc.getTextWidth(label) + 2 : 0;
            const valueX = margin + 3 + (label ? labelWidth + 1 : 0);
            const maxValueW = contentW - labelWidth - 8;
            const wrapped = doc.splitTextToSize(value, maxValueW);
            doc.text(wrapped, valueX, y + 4);
            y += wrapped.length * 5 + 1;
        });

        // Draw the border around the conditions box
        doc.rect(margin, boxStartY - 1, contentW, y - boxStartY + 3, 'S');

        y += 6;

        // ── FOOTER ──
        const footerY = pageH - 18;

        // Colorful gradient stripe (brand colors: purple → pink → orange → yellow → green)
        // Simulated by drawing many thin vertical segments across the full page width
        const stripeY = footerY - 5;
        const stripeH = 1.8;
        const stripeW = pageW; // full page width
        const segments = 120;
        const segW = stripeW / segments;

        // Color stops: [R, G, B]
        const colorStops: [number, number, number][] = [
            [97, 37, 123],   // 0%   — Comarca purple
            [193, 52, 139],   // 20%  — Comarca pink/magenta
            [234, 132, 6],   // 45%  — Comarca orange
            [255, 217, 124],  // 65%  — Comarca yellow
            [100, 200, 150],  // 82%  — soft green
            [80, 190, 210],  // 100% — soft teal
        ];

        const interpolateColor = (t: number): [number, number, number] => {
            const scaled = t * (colorStops.length - 1);
            const i = Math.min(Math.floor(scaled), colorStops.length - 2);
            const frac = scaled - i;
            const [r1, g1, b1] = colorStops[i];
            const [r2, g2, b2] = colorStops[i + 1];
            return [
                Math.round(r1 + (r2 - r1) * frac),
                Math.round(g1 + (g2 - g1) * frac),
                Math.round(b1 + (b2 - b1) * frac),
            ];
        };

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0);
        for (let i = 0; i < segments; i++) {
            const t = i / (segments - 1);
            const [r, g, b] = interpolateColor(t);
            doc.setFillColor(r, g, b);
            doc.rect(i * segW, stripeY, segW + 0.5, stripeH, 'F');
        }

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('DIRECCIÓN', margin, footerY + 2);
        doc.text(primeraFinal.datos_empresa?.direccion || primeraFinal.sucursal, margin, footerY + 5.5);

        doc.text('CONTACTO', pageW / 2, footerY + 2, { align: 'center' });
        doc.text(primeraFinal.datos_empresa?.email || '', pageW / 2, footerY + 5.5, { align: 'center' });

        doc.text('CELULAR', pageW - margin, footerY + 2, { align: 'right' });
        doc.text(primeraFinal.datos_empresa?.telefono || '', pageW - margin, footerY + 5.5, { align: 'right' });

        return doc.output('blob');
    };

